import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BOOKS_META from '../data/booksMeta';
import KJV_BIBLE from '../data/kjvBible';
import { formatVerse } from '../data/formatVerse';

function getBookName(id) {
  const b = BOOKS_META.find(b => b.id === id);
  return b ? b.name : '';
}

function getChapterCount(id) {
  const b = BOOKS_META.find(b => b.id === id);
  return b ? b.chapters : 0;
}

export default function BibleReader() {
  const { bookId, chapter } = useParams();
  const navigate = useNavigate();
  const [highlighted, setHighlighted] = useState({});

  const bid = bookId ? parseInt(bookId) : null;
  const ch = chapter ? parseInt(chapter) : null;

  // View mode: 'books' | 'chapters' | 'text'
  const viewMode = ch ? 'text' : bid ? 'chapters' : 'books';

  const goBook = useCallback((id) => navigate(`/bible/${id}`), [navigate]);
  const goChapter = useCallback((b, c) => navigate(`/bible/${b}/${c}`), [navigate]);

  const prevCh = useCallback(() => {
    if (!bid || !ch) return;
    if (ch > 1) {
      goChapter(bid, ch - 1);
    } else if (bid > 1) {
      const prevBook = bid - 1;
      goChapter(prevBook, getChapterCount(prevBook));
    }
  }, [bid, ch, goChapter]);

  const nextCh = useCallback(() => {
    if (!bid || !ch) return;
    const maxCh = getChapterCount(bid);
    if (ch < maxCh) {
      goChapter(bid, ch + 1);
    } else if (bid < 66) {
      goChapter(bid + 1, 1);
    }
  }, [bid, ch, goChapter]);

  // Reset highlights when chapter changes
  useEffect(() => { setHighlighted({}); }, [bid, ch]);

  // Scroll to top when chapter loads
  useEffect(() => {
    if (viewMode === 'text') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [bid, ch, viewMode]);

  const toggleHighlight = (v) => {
    setHighlighted(prev => ({ ...prev, [v]: !prev[v] }));
  };

  const hasPrev = bid && ch && (ch > 1 || bid > 1);
  const hasNext = bid && ch && (ch < getChapterCount(bid) || bid < 66);

  return (
    <section>
      <div className="inner">
        {/* Header with selects */}
        <div className="bible-header">
          <div className="section-label">The Authorized King James Version</div>
          <h2 className="section-title" style={{ marginBottom: 0 }}>Read the Bible</h2>
          <div className="bible-nav-bar">
            <select
              value={bid || ''}
              onChange={(e) => {
                const val = e.target.value;
                if (val) goBook(parseInt(val));
                else navigate('/bible');
              }}
            >
              <option value="">Select Book</option>
              {BOOKS_META.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <select
              value={ch || ''}
              onChange={(e) => {
                if (e.target.value && bid) goChapter(bid, parseInt(e.target.value));
              }}
              disabled={!bid}
            >
              <option value="">Select Chapter</option>
              {bid && Array.from({ length: getChapterCount(bid) }, (_, i) => (
                <option key={i + 1} value={i + 1}>Chapter {i + 1}</option>
              ))}
            </select>
            {viewMode === 'text' && (
              <>
                <button className="nav-btn" disabled={!hasPrev} onClick={prevCh}>◄ Prev</button>
                <button className="nav-btn" disabled={!hasNext} onClick={nextCh}>Next ►</button>
              </>
            )}
          </div>
        </div>

        {/* BOOK GRID */}
        {viewMode === 'books' && (
          <div>
            <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '.9rem', letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '.5rem' }}>
              Old Testament
            </h3>
            <div className="book-grid">
              {BOOKS_META.filter(b => b.testament === 'OT').map(b => (
                <div key={b.id} className="book-btn ot" onClick={() => goBook(b.id)}>
                  {b.name}
                </div>
              ))}
            </div>
            <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '.9rem', letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', margin: '2rem 0 .5rem' }}>
              New Testament
            </h3>
            <div className="book-grid">
              {BOOKS_META.filter(b => b.testament === 'NT').map(b => (
                <div key={b.id} className="book-btn nt" onClick={() => goBook(b.id)}>
                  {b.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CHAPTER GRID */}
        {viewMode === 'chapters' && bid && (
          <div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', marginBottom: '.5rem' }}>
              {getBookName(bid)}
            </h3>
            <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '.8rem', letterSpacing: 2, color: 'var(--gold)', marginBottom: '.5rem' }}>
              SELECT A CHAPTER
            </p>
            <div className="chapter-grid">
              {Array.from({ length: getChapterCount(bid) }, (_, i) => (
                <div key={i + 1} className="ch-btn" onClick={() => goChapter(bid, i + 1)}>
                  {i + 1}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TEXT VIEW */}
        {viewMode === 'text' && bid && ch && (() => {
          const data = KJV_BIBLE[String(bid)]?.[String(ch)];
          if (!data) return <p>Chapter not found.</p>;
          return (
            <>
              <div className="bible-text">
                <div className="bible-chapter-heading">
                  {getBookName(bid)} {ch}
                </div>
                {data.map((text, i) => {
                  const vNum = i + 1;
                  return (
                    <p
                      key={vNum}
                      className={`bible-verse-line${highlighted[vNum] ? ' highlighted' : ''}`}
                      onClick={() => toggleHighlight(vNum)}
                    >
                      <span className="verse-num">{vNum}</span>{' '}
                      {formatVerse(text)}
                    </p>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 750, margin: '0 auto 3rem', flexWrap: 'wrap', gap: '1rem' }}>
                <button className="nav-btn" disabled={!hasPrev} onClick={prevCh}>◄ Previous Chapter</button>
                <button className="nav-btn" onClick={() => navigate(`/bible/${bid}`)}>All Chapters</button>
                <button className="nav-btn" disabled={!hasNext} onClick={nextCh}>Next Chapter ►</button>
              </div>
            </>
          );
        })()}
      </div>
    </section>
  );
}
